/**
 * SwarmProjectView — a ChatGPT-style project PAGE rendered inside a swarm's
 * main area when a project is opened. Shows the project title, a "new task in
 * this project" prompt (auto-filed), the project's chats/tasks, its Sources
 * (uploaded documents → always-used grounding), and editable Instructions.
 *
 * Props: projectId, theme(T), refreshSignal, onStartTask(query), onOpenTask(run_id), onBack()
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FolderKanban, ArrowLeft, Send, UploadCloud, FileText, Trash2, Loader2, MessageSquare,
  BookOpen, Save, CheckCircle2, Clock, Cloud, HardDrive, RefreshCw, BarChart3, TrendingUp, Users, Lock, Brain } from "lucide-react";
import ConnectorImportModal from "./ConnectorImportModal";
import ProjectKnowledgePanel from "../kb/ProjectKnowledgePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SwarmProjectView({ projectId, theme: T, refreshSignal, onStartTask, onOpenTask, onBack }) {
  const [project, setProject] = useState(null);
  const [q, setQ] = useState("");
  const [instr, setInstr] = useState("");
  const [instrDirty, setInstrDirty] = useState(false);
  const [savingInstr, setSavingInstr] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [connStatus, setConnStatus] = useState(null);
  const [importProvider, setImportProvider] = useState(null);
  const [insights, setInsights] = useState(null);
  const [syncs, setSyncs] = useState([]);
  const [syncingId, setSyncingId] = useState(null);
  const [extractingId, setExtractingId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}`);
      setProject(data);
      setInstr((prev) => (instrDirty ? prev : (data.instructions || "")));
    } catch (_) { toast.error("Could not load project"); }
  }, [projectId, instrDirty]);

  const loadConnStatus = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/connectors/status`); setConnStatus(data); }
    catch (_) { setConnStatus(null); }
  }, []);

  const loadInsights = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/projects/${projectId}/insights`); setInsights(data); }
    catch (_) { setInsights(null); }
  }, [projectId]);

  const loadSyncs = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/connectors/sync?project_id=${projectId}`); setSyncs(data.syncs || []); }
    catch (_) { setSyncs([]); }
  }, [projectId]);

  useEffect(() => { load(); loadConnStatus(); loadInsights(); loadSyncs(); }, [load, loadConnStatus, loadInsights, loadSyncs, refreshSignal]);

  const runSync = async (syncId) => {
    setSyncingId(syncId);
    try {
      const { data } = await axios.post(`${API}/connectors/sync/${syncId}/run`);
      toast.success(data.ingested > 0 ? `Synced — ${data.ingested} new file(s)` : "Already up to date");
      loadSyncs(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    setSyncingId(null);
  };
  const removeSync = async (syncId) => {
    try { await axios.delete(`${API}/connectors/sync/${syncId}`); loadSyncs(); }
    catch (_) { toast.error("Could not stop sync"); }
  };

  // Handle OAuth return: ?connected=google&project=<id>&open_import=google
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const connected = p.get("connected");
    const err = p.get("connect_error");
    const proj = p.get("project");
    const openImport = p.get("open_import");
    if (!connected && !err) return;
    if (proj && proj !== projectId) return;
    if (connected) {
      toast.success(`${connected === "google" ? "Google Drive" : "SharePoint"} connected`);
      loadConnStatus();
      if (openImport) setImportProvider(openImport);
    } else if (err) {
      toast.error("Could not connect. Please try again.");
    }
    const url = new URL(window.location.href);
    ["connected", "connect_error", "project", "open_import"].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
  }, [projectId, loadConnStatus]);

  const connect = async (provider) => {
    try {
      const returnTo = window.location.pathname;
      const { data } = await axios.get(`${API}/connectors/${provider}/auth-url?project_id=${projectId}&return_to=${encodeURIComponent(returnTo)}`);
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start connection"); }
  };

  const onConnectorClick = (provider) => {
    const st = connStatus?.[provider];
    if (!st?.configured) { toast.error(`${provider === "google" ? "Google Drive" : "SharePoint"} is not configured`); return; }
    if (st.connected) setImportProvider(provider);
    else connect(provider);
  };

  const pendingKey = (project?.knowledge_sources || []).map((s) => `${s.id}:${s.status}`).join(",");
  useEffect(() => {
    const pending = (project?.knowledge_sources || []).some((s) => !["ready", "failed"].includes(s.status));
    if (!pending) return;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [pendingKey, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => { if (!q.trim()) return; onStartTask(q.trim()); setQ(""); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("title", file.name);
      await axios.post(`${API}/projects/${projectId}/knowledge/upload`, fd);
      toast.success("Added to project knowledge");
      setTimeout(load, 1200); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const delSource = async (sid) => {
    try { await axios.delete(`${API}/projects/${projectId}/knowledge/${sid}`); load(); }
    catch (_) { toast.error("Could not delete"); }
  };
  const removeTask = async (tid) => {
    try { await axios.delete(`${API}/projects/tasks/${tid}`); load(); }
    catch (_) { toast.error("Could not remove"); }
  };
  const extractTask = async (t) => {
    if (extractingId) return;
    setExtractingId(t.run_id);
    try {
      const { data } = await axios.post(`${API}/kb/ingest/from-run`, {
        swarm: project.swarm, run_id: t.run_id, project_id: projectId,
      });
      if (data.created > 0) toast.success(`${data.created} artifacts extracted — review in the Knowledge Base queue`);
      else toast.info(data.message || "No new knowledge extracted");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Extraction failed");
    } finally { setExtractingId(null); }
  };
  const saveInstr = async () => {
    setSavingInstr(true);
    try { await axios.patch(`${API}/projects/${projectId}`, { instructions: instr }); setInstrDirty(false); toast.success("Instructions saved"); load(); }
    catch (_) { toast.error("Could not save"); }
    setSavingInstr(false);
  };
  const toggleShare = async () => {
    const next = !project.shared;
    try {
      await axios.patch(`${API}/projects/${projectId}`, { shared: next });
      toast.success(next ? "Project shared with your team" : "Project set to private");
      load();
    } catch (_) { toast.error("Could not update sharing"); }
  };

  const card = { background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 18 };
  const label = { fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: T.textDim, fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 7 };
  const fmtUsd = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  if (!project) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: T.textDim }}><Loader2 className="animate-spin" /></div>;
  }

  const sources = project.knowledge_sources || [];
  const tasks = project.tasks || [];

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "28px 24px 60px" }} data-testid="swarm-project-view">
      <button onClick={onBack} data-testid="project-view-back"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 13, marginBottom: 18 }}>
        <ArrowLeft size={15} /> Back to swarm
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: `${T.accent}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FolderKanban size={26} color={T.accent} />
        </div>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: T.textH, margin: 0 }} data-testid="project-view-name">{project.name}</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
            {project.category ? <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 7, background: T.bg, border: `1px solid ${T.border}`, color: T.textDim }}>{project.category}</span> : null}
            <span style={{ fontSize: 12.5, color: T.textDim }}>{tasks.length} task{tasks.length !== 1 ? "s" : ""} · {sources.length} source{sources.length !== 1 ? "s" : ""}</span>
            {project.is_owner ? (
              <button onClick={toggleShare} data-testid="project-view-share-toggle"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  padding: "3px 10px", borderRadius: 20, border: `1px solid ${project.shared ? `${T.accent}66` : T.border}`,
                  background: project.shared ? `${T.accent}14` : T.bg, color: project.shared ? T.accent : T.textDim }}>
                {project.shared ? <Users size={13} /> : <Lock size={12} />} {project.shared ? "Shared with team" : "Private"}
              </button>
            ) : (
              <span data-testid="project-view-shared-by" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "3px 10px", borderRadius: 20, border: `1px solid ${T.accent}44`, background: `${T.accent}12`, color: T.accent }}>
                <Users size={13} /> Shared by {project.created_by_name || "a teammate"}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ ...card, marginTop: 18, display: "flex", gap: 10, alignItems: "center" }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") start(); }}
          placeholder={`New task in ${project.name} — ask a question…`} data-testid="project-view-prompt"
          style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: T.textH, fontSize: 15 }} />
        <button onClick={start} disabled={!q.trim()} data-testid="project-view-send"
          style={{ display: "flex", alignItems: "center", gap: 7, background: T.accent, border: "none", borderRadius: 10, padding: "10px 16px", color: "#04211A", fontWeight: 700, fontSize: 14, cursor: "pointer", opacity: q.trim() ? 1 : 0.5 }}>
          <Send size={15} /> Ask
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16, marginTop: 16, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {insights && (
          <div style={card} data-testid="project-view-insights">
            <div style={label}><BarChart3 size={13} /> Insights</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              {[["Runs", insights.runs.total, false], ["Completed", insights.runs.completed, false], ["Value found", fmtUsd(insights.runs.total_value_found), true]].map(([l, v, acc]) => (
                <div key={l} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textDim, fontWeight: 700 }}>{l}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: acc ? T.accent : T.textH, marginTop: 3 }} data-testid={`insight-${l.toLowerCase().replace(/ /g, "-")}`}>{v}</div>
                </div>
              ))}
            </div>
            {(insights.knowledge.top_sources || []).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textDim, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><TrendingUp size={11} /> Top knowledge sources</div>
                {insights.knowledge.top_sources.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12, color: T.textB, padding: "3px 0" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                    <span style={{ fontSize: 11, color: T.textDim, flex: "0 0 auto" }}>{s.chunk_count} chunks</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={card}>
          <div style={label}><MessageSquare size={13} /> Chats in this project</div>
          {tasks.length === 0 ? (
            <div style={{ fontSize: 13, color: T.textDim }} data-testid="project-view-tasks-empty">No tasks yet. Ask a question above and it will be filed here.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="project-view-tasks">
              {tasks.map((t) => (
                <div key={t.id} data-testid={`project-view-task-${t.id}`}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "11px 13px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bg, cursor: "pointer" }}
                  onClick={() => onOpenTask(t.run_id)}>
                  <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                    <MessageSquare size={14} color={T.textDim} />
                    <span style={{ fontSize: 13.5, color: T.textH, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.task_title || t.run_id}</span>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4, flex: "0 0 auto" }}>
                    <button onClick={(e) => { e.stopPropagation(); extractTask(t); }} data-testid={`project-view-task-extract-${t.id}`}
                      title="Extract knowledge to review queue"
                      style={{ background: "none", border: "none", cursor: "pointer", color: extractingId === t.run_id ? T.accent : T.textDim, display: "flex" }}>
                      {extractingId === t.run_id ? <Loader2 size={13} className="animate-spin" /> : <Brain size={13} />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); removeTask(t.id); }} data-testid={`project-view-task-remove-${t.id}`}
                      style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, display: "flex" }}><Trash2 size={13} /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={card}>
            <div style={{ ...label, justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 7 }}><BookOpen size={13} /> Sources</span>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="project-view-upload"
                style={{ display: "flex", alignItems: "center", gap: 6, background: `${T.accent}14`, border: `1px solid ${T.accent}55`, borderRadius: 8, padding: "5px 10px", color: T.accent, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />} Add files
              </button>
              <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.md,.csv,.html,.pptx"
                onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} data-testid="project-view-file-input" />
            </div>

            {connStatus && (connStatus.google?.configured || connStatus.microsoft?.configured) && (
              <div style={{ marginBottom: 12 }} data-testid="project-view-connectors">
                <div style={{ display: "flex", gap: 8 }}>
                  {connStatus.google?.configured && (
                    <button onClick={() => onConnectorClick("google")} data-testid="project-view-connect-google"
                      title={connStatus.google.connected ? `Google Drive · ${connStatus.google.email || "connected"}` : "Connect Google Drive"}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 9, padding: "8px 10px", color: T.textB, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                      <HardDrive size={14} color={T.accent} /> {connStatus.google.connected ? "Import from Drive" : "Connect Drive"}
                    </button>
                  )}
                  {connStatus.microsoft?.configured && (
                    <button onClick={() => onConnectorClick("microsoft")} data-testid="project-view-connect-microsoft"
                      title={connStatus.microsoft.connected ? `SharePoint · ${connStatus.microsoft.email || "connected"}` : "Connect SharePoint"}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 9, padding: "8px 10px", color: T.textB, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                      <Cloud size={14} color={T.accent} /> {connStatus.microsoft.connected ? "Import from SharePoint" : "Connect SharePoint"}
                    </button>
                  )}
                </div>
                {(connStatus.google?.connected || connStatus.microsoft?.connected) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 6 }} data-testid="project-view-connector-accounts">
                    {connStatus.google?.connected && connStatus.google.email && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: T.textDim }} data-testid="connector-email-google">
                        <CheckCircle2 size={11} color="#10B981" /> Drive: {connStatus.google.email}
                      </div>
                    )}
                    {connStatus.microsoft?.connected && connStatus.microsoft.email && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: T.textDim }} data-testid="connector-email-microsoft">
                        <CheckCircle2 size={11} color="#10B981" /> SharePoint: {connStatus.microsoft.email}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {syncs.length > 0 && (
              <div style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 }} data-testid="project-view-syncs">
                <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textDim, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  <RefreshCw size={11} /> Auto-synced folders
                </div>
                {syncs.map((s) => (
                  <div key={s.id} data-testid={`project-view-sync-${s.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", borderRadius: 9, border: `1px solid ${T.border}`, background: T.bg }}>
                    {s.provider === "google" ? <HardDrive size={13} color={T.accent} /> : <Cloud size={13} color={T.accent} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: T.textH, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.folder_name}</div>
                      <div style={{ fontSize: 10, color: T.textDim }}>{s.file_count || 0} file{(s.file_count || 0) !== 1 ? "s" : ""}{s.last_synced ? ` · synced ${new Date(s.last_synced).toLocaleString()}` : ""}</div>
                    </div>
                    <button onClick={() => runSync(s.id)} disabled={syncingId === s.id} data-testid={`project-view-sync-now-${s.id}`}
                      title="Sync now" style={{ background: "none", border: "none", cursor: "pointer", color: T.accent, display: "flex" }}>
                      {syncingId === s.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                    </button>
                    <button onClick={() => removeSync(s.id)} data-testid={`project-view-sync-remove-${s.id}`}
                      title="Stop syncing" style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, display: "flex" }}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            {sources.length === 0 ? (
              <div style={{ fontSize: 12.5, color: T.textDim }} data-testid="project-view-sources-empty">
                Add PDFs, docs or notes. The swarm always uses them when answering in this project.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="project-view-sources">
                {sources.map((s) => (
                  <div key={s.id} data-testid={`project-view-source-${s.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 9, border: `1px solid ${T.border}`, background: T.bg }}>
                    <FileText size={14} color={T.textDim} />
                    <span style={{ flex: 1, fontSize: 12.5, color: T.textH, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                    {s.status === "ready" ? <CheckCircle2 size={13} color="#10B981" /> : <Clock size={13} color="#F59E0B" />}
                    <button onClick={() => delSource(s.id)} data-testid={`project-view-source-remove-${s.id}`}
                      style={{ background: "none", border: "none", cursor: "pointer", color: T.textDim, display: "flex" }}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={card}>
            <div style={label}><Save size={13} /> Instructions</div>
            <textarea value={instr} onChange={(e) => { setInstr(e.target.value); setInstrDirty(true); }} rows={5}
              placeholder="Give the swarm standing instructions for this project (persona, priorities, rules)…"
              data-testid="project-view-instructions"
              style={{ width: "100%", resize: "vertical", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 9, padding: "10px 12px", fontSize: 13, color: T.textH, outline: "none", lineHeight: 1.5 }} />
            <button onClick={saveInstr} disabled={savingInstr || !instrDirty} data-testid="project-view-save-instructions"
              style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 7, background: instrDirty ? T.accent : T.bg, border: `1px solid ${T.border}`, borderRadius: 9, padding: "8px 14px", color: instrDirty ? "#04211A" : T.textDim, fontSize: 13, fontWeight: 600, cursor: instrDirty ? "pointer" : "default", opacity: savingInstr ? 0.6 : 1 }}>
              {savingInstr ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save instructions
            </button>
          </div>

          <ProjectKnowledgePanel projectId={projectId} swarm={project.swarm} vertical={project.swarm === "csh" ? "grant" : project.swarm === "rightsline" ? "licensing" : project.swarm === "contentry" ? "brand" : project.swarm} />
        </div>
      </div>

      {importProvider && (
        <ConnectorImportModal
          provider={importProvider}
          projectId={projectId}
          onClose={() => setImportProvider(null)}
          onImported={() => { setImportProvider(null); setTimeout(load, 1200); load(); loadSyncs(); }}
        />
      )}
    </div>
  );
}
